import { useEffect } from "react";
import { useParams } from "react-router";
import { Box, Paper, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ROUTES } from "../../routes";
import LoadingOverlay from "../Shared/LoadingOverlay";
import SubscriptionInvoiceItem from "./SubscriptionInvoiceItem";
import { useSubscriptionInvoices } from "./useSubscriptionInvoices";
import SubscriptionInvoicesHeader from "./SubscriptionInvoicesHeader";

export default function SubscriptionInvoices() {
    const { uniqueIdentifier = "" } = useParams<{ uniqueIdentifier: string }>();
    const { data, isLoading } = useSubscriptionInvoices(uniqueIdentifier);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        document.title = `${ROUTES.SUBSCRIPTION_INVOICES.pageTitle} - Client Bookings Zanda`;
    }, []);

    if (isLoading) {
        return <LoadingOverlay />;
    }

    const contentSx = {
        maxWidth: isMobile ? "100%" : theme.breakpoints.values.md,
        width: "100%",
        mx: "auto",
    };

    if (data?.isLinkExpired) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
                <SubscriptionInvoicesHeader logoUrl={data.businessLogoUrl} name={data.businessName} />
                <Box sx={contentSx} p={isMobile ? 2 : 4} data-test-id="section_subscription_invoices_expired">
                    <Typography variant="body1" textAlign="center">
                        This link has expired. Please contact support for assistance at support@zandahealth.com
                    </Typography>
                </Box>
            </Box>
        );
    }

    const invoices = data?.invoices ?? [];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
            <SubscriptionInvoicesHeader logoUrl={data?.businessLogoUrl} name={data?.businessName} />
            <Stack spacing={2} sx={contentSx} p={isMobile ? 2 : 4} data-test-id="section_subscription_invoices">
                <Typography variant="h6" fontWeight="bold">
                    {ROUTES.SUBSCRIPTION_INVOICES.pageTitle}
                </Typography>
                <Paper>
                    <Stack spacing={2} p={3}>
                        {invoices.length > 0 ? (
                            <Stack spacing={1} sx={{ overflow: "hidden", minWidth: 0 }}>
                                {invoices.map((invoice) => (
                                    <SubscriptionInvoiceItem key={invoice.id} invoice={invoice} />
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" data-test-id="text_no_subscription_invoices">
                                No invoices found.
                            </Typography>
                        )}
                    </Stack>
                </Paper>
            </Stack>
        </Box>
    );
}
